import { useState, useEffect, useCallback } from 'react';
import { FileText, Users, Briefcase, Download, ChevronDown, ChevronUp, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Admin = () => {
  const [tab, setTab] = useState('students');
  const [students, setStudents] = useState([]);
  const [careers, setCareers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [sRes, cRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/submissions/students`),
        fetch(`${API_URL}/api/admin/submissions/careers`),
      ]);
      if (sRes.ok) setStudents(await sRes.json());
      if (cRes.ok) setCareers(await cRes.json());
    } catch (err) {
      console.error('Failed to fetch submissions:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const data = tab === 'students' ? students : careers;

  const formatDate = (iso) => {
    const d = new Date(iso);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) + ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <main className="min-h-screen bg-[var(--background)]" data-testid="admin-page">
      {/* Header */}
      <div className="bg-[var(--primary)] py-6">
        <div className="container-main">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[var(--secondary)] text-[11px] font-bold uppercase tracking-[0.2em] mb-1">Admin Panel</p>
              <h1 className="text-2xl font-bold text-white tracking-tight">Form Submissions</h1>
            </div>
            <button
              onClick={fetchData}
              disabled={loading}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white text-xs font-bold uppercase tracking-wider transition-colors"
              data-testid="admin-refresh-btn"
            >
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-[var(--border)]">
        <div className="container-main flex gap-0">
          <button
            onClick={() => setTab('students')}
            className={`flex items-center gap-2 px-6 py-3 text-sm font-semibold border-b-2 transition-colors ${
              tab === 'students' ? 'border-[var(--secondary)] text-[var(--primary)]' : 'border-transparent text-[var(--text-secondary)] hover:text-[var(--primary)]'
            }`}
            data-testid="admin-tab-students"
          >
            <Users size={16} />
            Student Evaluations
            <span className="ml-1 px-2 py-0.5 bg-[var(--background)] text-xs font-bold rounded-full">{students.length}</span>
          </button>
          <button
            onClick={() => setTab('careers')}
            className={`flex items-center gap-2 px-6 py-3 text-sm font-semibold border-b-2 transition-colors ${
              tab === 'careers' ? 'border-[var(--secondary)] text-[var(--primary)]' : 'border-transparent text-[var(--text-secondary)] hover:text-[var(--primary)]'
            }`}
            data-testid="admin-tab-careers"
          >
            <Briefcase size={16} />
            Career Applications
            <span className="ml-1 px-2 py-0.5 bg-[var(--background)] text-xs font-bold rounded-full">{careers.length}</span>
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="container-main py-6">
        {loading ? (
          <div className="text-center py-12 text-[var(--text-secondary)]" data-testid="admin-loading">
            <RefreshCw size={24} className="animate-spin mx-auto mb-3 text-[var(--secondary)]" />
            <p className="text-sm">Loading submissions...</p>
          </div>
        ) : data.length === 0 ? (
          <div className="text-center py-12 text-[var(--text-secondary)]" data-testid="admin-empty">
            <FileText size={32} className="mx-auto mb-3 opacity-40" />
            <p className="text-sm">No {tab === 'students' ? 'student evaluations' : 'career applications'} yet.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="admin-submissions-list">
            {data.map((item) => (
              <div key={item.id} className="bg-white border border-[var(--border)] transition-shadow hover:shadow-sm" data-testid={`submission-${item.id}`}>
                {/* Row */}
                <button
                  onClick={() => setExpandedId(expandedId === item.id ? null : item.id)}
                  className="w-full flex items-center justify-between px-5 py-4 text-left"
                  data-testid={`submission-toggle-${item.id}`}
                >
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="w-9 h-9 bg-[var(--background)] flex items-center justify-center flex-shrink-0">
                      <span className="text-[var(--secondary)] text-xs font-bold">
                        {item.first_name?.[0]}{item.last_name?.[0]}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-[var(--primary)] truncate">
                        {item.first_name} {item.last_name}
                      </p>
                      <p className="text-xs text-[var(--text-secondary)] truncate">{item.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 flex-shrink-0">
                    <span className="text-xs text-[var(--text-secondary)] hidden sm:block">{formatDate(item.created_at)}</span>
                    <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                      item.status === 'new' ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {item.status}
                    </span>
                    {item.files?.length > 0 && (
                      <span className="text-xs text-[var(--text-secondary)] flex items-center gap-1">
                        <FileText size={12} /> {item.files.length}
                      </span>
                    )}
                    {expandedId === item.id ? <ChevronUp size={16} className="text-[var(--text-secondary)]" /> : <ChevronDown size={16} className="text-[var(--text-secondary)]" />}
                  </div>
                </button>

                {/* Expanded Details */}
                {expandedId === item.id && (
                  <div className="border-t border-[var(--border)] px-5 py-4 bg-[var(--background)]" data-testid={`submission-details-${item.id}`}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                      <div>
                        <p className="text-[10px] font-bold uppercase text-[var(--text-secondary)] tracking-wider mb-0.5">Name</p>
                        <p className="text-sm text-[var(--primary)] font-medium">{item.first_name} {item.last_name}</p>
                      </div>
                      <div>
                        <p className="text-[10px] font-bold uppercase text-[var(--text-secondary)] tracking-wider mb-0.5">Email</p>
                        <a href={`mailto:${item.email}`} className="text-sm text-[var(--primary)] hover:text-[var(--secondary)] transition-colors">{item.email}</a>
                      </div>
                      <div>
                        <p className="text-[10px] font-bold uppercase text-[var(--text-secondary)] tracking-wider mb-0.5">Phone</p>
                        <p className="text-sm text-[var(--primary)]">{item.phone}</p>
                      </div>
                      <div>
                        <p className="text-[10px] font-bold uppercase text-[var(--text-secondary)] tracking-wider mb-0.5">Submitted</p>
                        <p className="text-sm text-[var(--primary)]">{formatDate(item.created_at)}</p>
                      </div>
                    </div>

                    {/* Files */}
                    {item.files?.length > 0 && (
                      <div>
                        <p className="text-[10px] font-bold uppercase text-[var(--text-secondary)] tracking-wider mb-2">Uploaded Files ({item.files.length})</p>
                        <div className="space-y-1.5">
                          {item.files.map((file) => (
                            <div key={file.id} className="flex items-center justify-between bg-white px-3 py-2 border border-[var(--border)]">
                              <div className="flex items-center gap-2 min-w-0">
                                <FileText size={14} className="text-[var(--secondary)] flex-shrink-0" />
                                <span className="text-sm text-[var(--primary)] truncate">{file.original_filename}</span>
                                <span className="text-xs text-[var(--text-secondary)] flex-shrink-0">
                                  ({(file.size / 1024).toFixed(0)} KB)
                                </span>
                              </div>
                              <a
                                href={`${API_URL}/api/files/${file.storage_path}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-1 text-xs text-[var(--secondary)] hover:text-[var(--primary)] font-semibold transition-colors flex-shrink-0 ml-3"
                                data-testid={`download-file-${file.id}`}
                              >
                                <Download size={12} />
                                Download
                              </a>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </main>
  );
};

export default Admin;
